import * as Phaser from "phaser";
import type { Writable } from "svelte/store";
import { writable } from "svelte/store";
import type { CancelablePromise } from "cancelable-promise";
import { PositionMessage_Direction } from "@workadventure/messages";
import type { PictureStore } from "../../Stores/PictureStore";
import { TexturesHelper } from "../Helpers/TexturesHelper";
import { PlayerAnimationTypes } from "../Player/Animation";
import { ProtobufClientUtils } from "../../Network/ProtobufClientUtils";
import { debugRepo } from "../../Utils/Debuggers";

import Sprite = Phaser.GameObjects.Sprite;
import Container = Phaser.GameObjects.Container;
import Body = Phaser.Physics.Arcade.Body;

/** [Trajetoria] animacao de pouso do companheiro (linhas 9-12 da folha) */
const COMPANION_LAND = "land";
/**
 * [Trajetoria] Tempo parado (ms) antes de pousar. Andando, o companheiro alcanca o
 * jogador, para um instante e sai de novo: sem essa espera ele pousaria e decolaria
 * o tempo todo. Nesse meio tempo continua na animacao de andar (bate asa no lugar).
 */
const COMPANION_LAND_DELAY = 900;

export interface CompanionStatus {
    x: number;
    y: number;
    name: string;
    moving: boolean;
    direction: PositionMessage_Direction;
}

export class Companion extends Container {
    public sprites: Map<string, Sprite>;

    private delta: number;
    private invisible: boolean;
    private updateListener: (time: number, delta: number) => void;
    private target: { x: number; y: number; direction: PositionMessage_Direction };

    private direction: PositionMessage_Direction;
    private animationType: PlayerAnimationTypes;
    private readonly _pictureStore: Writable<string | undefined>;
    private texturePromise: CancelablePromise<string | void> | undefined;
    /**
     * [Trajetoria] Ultima animacao pedida, para as transicoes (pousar/decolar) nao
     * serem atropeladas pelo step(), que roda a cada 128 ms.
     */
    private lastAnimationType: PlayerAnimationTypes | undefined;
    private lastDirectionStr: string | undefined;
    private idleSince: number | undefined;

    constructor(scene: Phaser.Scene, x: number, y: number, texturePromise: CancelablePromise<string>) {
        super(scene, x + 14, y + 4);

        this.sprites = new Map<string, Sprite>();

        this.delta = 0;
        this.invisible = true;
        this.target = { x, y, direction: PositionMessage_Direction.DOWN };

        this.direction = PositionMessage_Direction.DOWN;
        this.animationType = PlayerAnimationTypes.Idle;

        this._pictureStore = writable(undefined);

        this.texturePromise = texturePromise
            .then((resource) => {
                this.addResource(resource);
                this.invisible = false;
                return this.getSnapshot().then((htmlImageElementSrc) => {
                    this._pictureStore.set(htmlImageElementSrc);
                });
            })
            .catch((_) => debugRepo("No companion texture for this player"));

        this.scene.physics.world.enableBody(this);

        this.getBody().setImmovable(true);
        this.getBody().setCollideWorldBounds(false);
        this.setSize(16, 16);
        this.getBody().setSize(16, 16);
        this.getBody().setOffset(0, 8);

        this.setDepth(-1);

        this.updateListener = this.step.bind(this);
        this.scene.events.addListener("update", this.updateListener);

        this.scene.add.existing(this);
    }

    public setTarget(x: number, y: number, direction: PositionMessage_Direction) {
        this.target = { x, y: y + 4, direction };
    }

    public step(time: number, delta: number) {
        if (typeof this.target === "undefined") return;

        this.delta += delta;
        if (this.delta < 128) {
            return;
        }
        this.delta = 0;

        const xDist = this.target.x - this.x;
        const yDist = this.target.y - this.y;

        const distance = Math.pow(xDist, 2) + Math.pow(yDist, 2);

        if (distance < 650) {
            this.animationType = PlayerAnimationTypes.Idle;
            this.direction = this.target.direction;

            this.getBody().stop();
        } else {
            this.animationType = PlayerAnimationTypes.Walk;

            const xDir = xDist / Math.max(Math.abs(xDist), 1);
            const yDir = yDist / Math.max(Math.abs(yDist), 1);

            const speed = 256;
            this.getBody().setVelocity(
                Math.min(Math.abs(xDist * 2.5), speed) * xDir,
                Math.min(Math.abs(yDist * 2.5), speed) * yDir,
            );

            if (Math.abs(xDist) > Math.abs(yDist)) {
                if (xDist < 0) {
                    this.direction = PositionMessage_Direction.LEFT;
                } else {
                    this.direction = PositionMessage_Direction.RIGHT;
                }
            } else {
                if (yDist < 0) {
                    this.direction = PositionMessage_Direction.UP;
                } else {
                    this.direction = PositionMessage_Direction.DOWN;
                }
            }
        }

        this.setDepth(this.y);
        // [Trajetoria] espera antes de pousar (so para folha com a linha "pousar")
        if (this.animationType === PlayerAnimationTypes.Idle) {
            this.idleSince ??= time;
        } else {
            this.idleSince = undefined;
        }
        const waitingToLand =
            this.idleSince !== undefined && time - this.idleSince < COMPANION_LAND_DELAY && this.hasLandAnimation();
        this.playAnimation(this.direction, waitingToLand ? PlayerAnimationTypes.Walk : this.animationType);
    }

    public async getSnapshot(): Promise<string> {
        const sprites = Array.from(this.sprites.values()).map((sprite) => {
            return { sprite, frame: 1 };
        });
        return TexturesHelper.getSnapshot(this.scene, ...sprites).catch((reason) => {
            console.warn(reason);
            for (const sprite of this.sprites.values()) {
                // it can be either cat or dog prefix
                if (sprite.texture.key.includes("cat") || sprite.texture.key.includes("dog")) {
                    return this.scene.textures.getBase64(sprite.texture.key);
                }
            }
            return "cat1";
        });
    }

    private playAnimation(direction: PositionMessage_Direction, type: PlayerAnimationTypes): void {
        if (this.invisible) return;

        const directionStr = ProtobufClientUtils.toDirectionString(direction);
        const typeChanged = this.lastAnimationType !== undefined && this.lastAnimationType !== type;
        const sameRequest = this.lastAnimationType === type && this.lastDirectionStr === directionStr;
        this.lastAnimationType = type;
        this.lastDirectionStr = directionStr;

        for (const [resource, sprite] of this.sprites.entries()) {
            const key = `${resource}-${directionStr}-${type}`;
            // [Trajetoria] Folha com a linha "pousar" (>= 36 quadros): ao parar, toca o
            // pouso e depois o "parado"; ao voltar a andar, o pouso ao contrario (decolar).
            const landKey = `${resource}-${directionStr}-${COMPANION_LAND}`;
            // Enquanto a transicao toca, o pedido repetido do step() nao a interrompe
            // (o chain ja leva para a animacao certa).
            if (sameRequest && sprite.anims.isPlaying && sprite.anims.currentAnim?.key === landKey) {
                continue;
            }
            // Qualquer outra troca descarta o que estava encadeado (o Phaser nao limpa a
            // fila sozinho quando outra animacao interrompe a transicao).
            sprite.chain();
            if (typeChanged && this.scene.anims.exists(landKey)) {
                if (type === PlayerAnimationTypes.Idle) {
                    sprite.play(landKey);
                } else {
                    sprite.playReverse(landKey);
                }
                sprite.chain(key);
                continue;
            }
            sprite.play(key, true);
        }
    }

    private hasLandAnimation(): boolean {
        for (const resource of this.sprites.keys()) {
            if (this.scene.anims.exists(`${resource}-down-${COMPANION_LAND}`)) return true;
        }
        return false;
    }

    private addResource(resource: string, frame?: string | number): void {
        const sprite = new Sprite(this.scene, 0, 0, resource, frame);

        this.add(sprite);

        this.getAnimations(resource).forEach((animation) => {
            if (!animation.key || !this.scene.anims.exists(animation.key)) {
                this.scene.anims.create(animation);
            }
        });

        this.scene.sys.updateList.add(sprite);
        this.sprites.set(resource, sprite);
    }

    private getAnimations(resource: string): Phaser.Types.Animations.Animation[] {
        return [...this.getBaseAnimations(resource), ...this.getExtendedAnimations(resource)];
    }

    /**
     * [Trajetoria] Linhas extras opcionais na folha do companheiro (96 px de largura,
     * 3 quadros por linha, direcoes na ordem baixo, esquerda, direita, cima):
     * - linhas 5 a 8 (quadros 12-23): "parado" em loop, no lugar do quadro congelado;
     * - linhas 9 a 12 (quadros 24-35): "pousar", tocado uma vez ao parar (e ao contrario
     *   ao voltar a andar).
     * Folha antiga (96x128, 12 quadros) nao registra nada daqui e fica como era.
     */
    private getExtendedAnimations(resource: string): Phaser.Types.Animations.Animation[] {
        const texture = this.scene.textures.get(resource);
        // frameTotal conta tambem o quadro __BASE
        const frames = texture ? texture.frameTotal - 1 : 0;
        const directions = ["down", "left", "right", "up"];
        const animations: Phaser.Types.Animations.Animation[] = [];
        if (frames >= 36) {
            directions.forEach((direction, row) => {
                const first = 24 + row * 3;
                animations.push({
                    key: `${resource}-${direction}-${COMPANION_LAND}`,
                    frames: this.scene.anims.generateFrameNumbers(resource, { frames: [first, first + 1, first + 2] }),
                    frameRate: 10,
                    repeat: 0,
                });
            });
        }
        return animations;
    }

    private getBaseAnimations(resource: string): Phaser.Types.Animations.Animation[] {
        const texture = this.scene.textures.get(resource);
        const frames = texture ? texture.frameTotal - 1 : 0;
        // [Trajetoria] com a linha "parado" (>= 24 quadros) o idle vira um loop lento
        const idle = (row: number, fallback: number): Pick<Phaser.Types.Animations.Animation, "frames" | "frameRate" | "repeat"> =>
            frames >= 24
                ? {
                      frames: this.scene.anims.generateFrameNumbers(resource, {
                          frames: [12 + row * 3, 13 + row * 3, 14 + row * 3, 13 + row * 3],
                      }),
                      frameRate: 4,
                      repeat: -1,
                  }
                : { frames: this.scene.anims.generateFrameNumbers(resource, { frames: [fallback] }), frameRate: 10, repeat: 1 };
        return [
            {
                key: `${resource}-down-${PlayerAnimationTypes.Idle}`,
                ...idle(0, 1),
            },
            {
                key: `${resource}-left-${PlayerAnimationTypes.Idle}`,
                ...idle(1, 4),
            },
            {
                key: `${resource}-right-${PlayerAnimationTypes.Idle}`,
                ...idle(2, 7),
            },
            {
                key: `${resource}-up-${PlayerAnimationTypes.Idle}`,
                ...idle(3, 10),
            },
            {
                key: `${resource}-down-${PlayerAnimationTypes.Walk}`,
                frames: this.scene.anims.generateFrameNumbers(resource, { frames: [0, 1, 2] }),
                frameRate: 15,
                repeat: -1,
            },
            {
                key: `${resource}-left-${PlayerAnimationTypes.Walk}`,
                frames: this.scene.anims.generateFrameNumbers(resource, { frames: [3, 4, 5] }),
                frameRate: 15,
                repeat: -1,
            },
            {
                key: `${resource}-right-${PlayerAnimationTypes.Walk}`,
                frames: this.scene.anims.generateFrameNumbers(resource, { frames: [6, 7, 8] }),
                frameRate: 15,
                repeat: -1,
            },
            {
                key: `${resource}-up-${PlayerAnimationTypes.Walk}`,
                frames: this.scene.anims.generateFrameNumbers(resource, { frames: [9, 10, 11] }),
                frameRate: 15,
                repeat: -1,
            },
        ];
    }

    private getBody(): Body {
        const body = this.body;

        if (!(body instanceof Body)) {
            throw new Error("Container does not have arcade body");
        }

        return body;
    }

    public destroy(): void {
        this.texturePromise?.cancel();
        for (const sprite of this.sprites.values()) {
            if (this.scene) {
                this.scene.sys.updateList.remove(sprite);
            }
        }

        if (this.scene) {
            this.scene.events.removeListener("update", this.updateListener);
        }

        super.destroy();
    }

    public get pictureStore(): PictureStore {
        return this._pictureStore;
    }
}
